/**
 * User Service
 *
 * API service for platform user management operations.
 */

import { apiClient } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import type {
  ApiResponse,
  ListRequest,
  PlatformUserListResponse,
  UserDetails,
  CreateUserRequest,
  UpdateUserRequest,
} from '../types';

// ============================================
// User List & Details
// ============================================

/**
 * Get list of platform users with pagination
 */
export async function getUserList(
  params: ListRequest
): Promise<PlatformUserListResponse> {
  const response = await apiClient.post<ApiResponse<PlatformUserListResponse>>(
    API_ENDPOINTS.USER.LIST,
    params
  );
  return response.data.result;
}

/**
 * Get user by ID
 */
export async function getUserById(id: string): Promise<UserDetails> {
  const response = await apiClient.get<ApiResponse<UserDetails>>(
    API_ENDPOINTS.USER.GET_BY_ID(id)
  );
  return response.data.result;
}

// ============================================
// User CRUD Operations
// ============================================

/**
 * API error response structure
 */
interface ApiErrorResponse {
  status: string;
  statusCode: number;
  statusMessage: string | null;
  showMessage: string;
  result: null;
}

/**
 * Custom API error class
 */
export class ApiError extends Error {
  statusCode: number;
  showMessage: string;

  constructor(statusCode: number, showMessage: string) {
    super(showMessage);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.showMessage = showMessage;
  }
}

/**
 * Create a new platform user
 */
export async function createUser(data: CreateUserRequest): Promise<UserDetails> {
  const response = await apiClient.post<ApiResponse<UserDetails> | ApiErrorResponse>(
    API_ENDPOINTS.USER.CREATE,
    data
  );

  // Check for API-level failure
  if (response.data.status === 'fail' || response.data.status === 'error') {
    const errorResponse = response.data as ApiErrorResponse;
    throw new ApiError(
      errorResponse.statusCode,
      errorResponse.showMessage || 'An error occurred while creating user'
    );
  }

  return response.data.result as UserDetails;
}

/**
 * Update an existing user
 */
export async function updateUser(
  data: UpdateUserRequest
): Promise<UserDetails> {
  const response = await apiClient.post<ApiResponse<UserDetails>>(
    API_ENDPOINTS.USER.UPDATE,
    data
  );
  return response.data.result;
}

// ============================================
// Export all functions
// ============================================

export const userService = {
  getUserList,
  getUserById,
  createUser,
  updateUser,
};
